import { createApp, type Plugin } from 'vue';
import { createPinia } from 'pinia';
import { PerfectScrollbarPlugin } from 'vue3-perfect-scrollbar';
import { library } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { fas } from '@fortawesome/free-solid-svg-icons'
import { far } from '@fortawesome/free-regular-svg-icons'
import { createVuetify } from 'vuetify'
import * as components from 'vuetify/components'
import * as directives from 'vuetify/directives'
import { aliases, fa } from 'vuetify/iconsets/fa-svg'
import { fr } from 'vuetify/locale'
import { App, getRouter, vuetifyOptions, type IConfiguration } from '@fondationcss/ui';
import { getCustomRoutes } from './routes';
import { menus } from './sidebar-items';
import { WikiPlugin } from '@fondationcss/wiki'
import { ArkivSignaturePlugin } from '@fondationcss/arkiv-signature'
import '@fondationcss/arkiv-signature/dist/index.css'
import '@fondationcss/arkiv-signature/dist/style.css'

import 'vuetify/styles';
import '@fontsource/public-sans/400.css';
import '@fontsource/public-sans/500.css';
import '@fontsource/public-sans/600.css';
import '@fontsource/public-sans/700.css';
import '@fondationcss/ui/dist/style.css';
import '@fondationcss/wiki/dist/style.css';
import '@fondationcss/markdown/dist/style.css';
import '@fondationcss/markdown/dist/markdown.css';
import "@/styles.css";
import 'highlight.js/styles/github.css';

const config = {
  AppName: 'Arkiv',
  SiteNameShort: 'Arkiv',
  SiteNameLong: 'Arkiv',
  Copyright: 'Copyright © 2026',
  MenuGroupsOrientation: 'vertical',
  Menus: menus,  
  DefaultLayout: 'byrnu-red-app',
  ThemeColor: '#3460a8',
  EnableAccessRequestButton: false,
  Logo: '/images/arkiv.svg',
  SiteLogo: '/images/arkiv.svg',
  SiteLogoAlt: '/images/arkiv-alt.svg',
  // SiteLogo: '/images/arkiv.svg'  // Logo for login/init screens (URL)
  // SiteLogoSvg: '<svg>...</svg>'  // Logo for login/init screens (raw SVG - takes priority over SiteLogo and Logo)
  // SiteLogoAlt: '/images/arkiv-alt.svg'  // Logo for header (URL)
  // SiteLogoAltSvg: '<svg>...</svg>'  // Logo for header (raw SVG - takes priority over all others)
  // HideLoginLogo: true,  // Uncomment to hide the bottom logo on login page
} as IConfiguration;

const sdkOptions = {
  apiUrl: import.meta.env.VITE_API_URL,
};

library.add(fas)
library.add(far)

const vuetify = createVuetify({
    components,
    directives,
    ...vuetifyOptions,
    // Sans locale explicite, Vuetify retombe sur l'anglais et affiche
    // « No data available », « Rows per page », etc. dans une interface
    // autrement entièrement francisée.
    locale: {
      locale: 'fr',
      fallback: 'en',
      messages: { fr },
    },
    icons: {
      defaultSet: 'fa-svg',
      aliases,
      sets: {
        'fa-svg': fa,
      },
    },
  })
const app = createApp(App);
const router = getRouter(getCustomRoutes);
app.provide('config', config)
app.provide('sdkOptions', sdkOptions)
app.use(vuetify);
app.component('font-awesome-icon', FontAwesomeIcon)
app.use(createPinia());
app.use(PerfectScrollbarPlugin);
app.use(router);
// Les contraintes génériques de `Plugin` (Vue 3.5+) sont plus strictes que la
// signature exportée par ces paquets : on requalifie sans passer par `any`.
app.use(WikiPlugin as unknown as Plugin, { router })
app.use(ArkivSignaturePlugin as unknown as Plugin, { router })
app.mount('#app');

// Désactive les suggestions d'autofill du navigateur sur tous les champs :
// les formulaires Arkiv ne contiennent pas de données personnelles et les
// suggestions de Chrome y sont toujours hors contexte. Le `defaults` de Vuetify
// ne peut pas le faire (il n'applique que les props déclarées, et `autocomplete`
// est un attribut passé à l'input), d'où l'application au niveau du DOM.
// Un champ peut se soustraire en déclarant lui-même un attribut autocomplete.
new MutationObserver(() => {
  document
    .querySelectorAll('input:not([autocomplete]), textarea:not([autocomplete])')
    .forEach((el) => el.setAttribute('autocomplete', 'off'));
}).observe(document.body, { childList: true, subtree: true });
